import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { hostService, listingService, stayVocabularyService } from "@/lib/services";
import { extractErrorMessage } from "@/lib/api";
import { ImageUploader } from "@/components/image-uploader";
import { PillPicker } from "@/components/pill-picker";
import { AMENITY_OPTIONS, HOUSE_RULE_OPTIONS } from "@/lib/options";
import type { CancellationPolicyValue, CreateListingPayload, Property } from "@/types/api";

/**
 * The ladders a host can commit to. Mirrors `CancellationPolicy` server-side;
 * the wording is the same so a host and their guests read the same sentence.
 */
const CANCELLATION_POLICIES: { value: CancellationPolicyValue; label: string; summary: string }[] =
  [
    {
      value: "flexible",
      label: "Flexible",
      summary: "Free cancellation until 24 hours before check-in.",
    },
    {
      value: "moderate",
      label: "Moderate",
      summary: "Free until 5 days before, then 50% back until 24 hours before.",
    },
    {
      value: "firm",
      label: "Firm",
      summary: "Free until 30 days before, then 50% back until 7 days before.",
    },
    {
      value: "strict",
      label: "Strict",
      summary: "Free until 14 days before, then 50% back until 7 days before.",
    },
    {
      value: "non_refundable",
      label: "Non-refundable",
      summary: "No refund at any point. Usually paired with a lower nightly rate.",
    },
  ];

export const Route = createFileRoute("/_authenticated/host/listings/new")({
  component: NewListingPage,
});

function NewListingPage() {
  const navigate = useNavigate();
  const propsQ = useQuery({
    queryKey: ["host-properties"],
    queryFn: () => hostService.properties(),
  });
  /*
    Every property, verified or not.

    This used to filter to `p.verified`, which meant a host who had just passed
    identity verification still could not list anything until an admin got to
    their address — an indefinite wait, with an empty screen for an answer. We
    already know who they are; what is unconfirmed is the address, and that is
    a thing to disclose rather than a reason to block. Listings on an
    unverified property carry a marker wherever they appear.
  */
  const properties = (propsQ.data as { data?: Property[] } | undefined)?.data ?? [];

  const { data: vocabulary } = useQuery({
    queryKey: ["stay-vocabulary"],
    queryFn: ({ signal }) => stayVocabularyService.get(signal),
    staleTime: Infinity,
  });
  const roomTypes = vocabulary?.room_types ?? [];

  const [form, setForm] = useState<CreateListingPayload>({
    property_id: "",
    title: "",
    description: "",
    price_per_night: 50000,
    room_type: "entire_place",
    max_guests: 2,
    bedrooms: 1,
    beds: 1,
    bathrooms: 1,
    images: [],
    amenities: [],
    house_rules: [],
    instant_book: false,
    cancellation_policy: "moderate",
    is_active: true,
  });

  const create = useMutation({
    mutationFn: (p: CreateListingPayload) => listingService.create(p),
    onSuccess: (res) => {
      toast.success("Listing created.");
      navigate({ to: "/listings/$listingId", params: { listingId: res.data.id } });
    },
    onError: (err) => toast.error(extractErrorMessage(err, "Couldn't create listing")),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.property_id) return toast.error("Choose a property.");
    if (form.description.length < 20)
      return toast.error("Description must be at least 20 characters.");
    if (form.price_per_night < 10) return toast.error("Price must be at least 10.");
    if ((form.images?.length ?? 0) === 0) return toast.error("Add at least one photo.");
    create.mutate(form);
  };

  if (propsQ.isLoading) {
    return <div className="mx-auto max-w-2xl px-4 py-8 text-sm text-ink-muted">Loading…</div>;
  }

  if (properties.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-4 py-12 text-center">
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Add a property first</h1>
        <p className="mt-2 text-sm text-ink-muted">
          A listing is a bookable unit inside a property, so there has to be a property to put it
          in. You can start listing right away — verification happens alongside.
        </p>
        <Link
          to="/host/properties/new"
          className="mt-5 inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
        >
          Add a property
        </Link>
      </div>
    );
  }

  const selected = properties.find((p) => p.id === form.property_id);

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <h1 className="text-3xl font-extrabold tracking-tight text-ink">Add a listing</h1>
      <p className="mt-1 text-sm text-ink-muted">A bookable unit within one of your properties.</p>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <Field label="Property" required>
          <select
            value={form.property_id}
            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            className="input"
            required
          >
            <option value="">— Select —</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.verified ? p.name : `${p.name} — awaiting verification`}
              </option>
            ))}
          </select>
          {/*
            Said at the moment of choosing, not discovered later. A host should
            know before they write the listing that it will carry a marker.
          */}
          {selected && !selected.verified && (
            <p className="mt-2 rounded-xl border border-accent bg-accent/15 px-3 py-2 text-xs leading-relaxed text-ink">
              <span className="font-bold">{selected.name} is not verified yet.</span> You can
              publish and take bookings now — guests will just see this listing marked as awaiting a
              property check until we finish it.
            </p>
          )}
        </Field>
        <Field label="Title" required>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="input"
            maxLength={255}
            placeholder="Sunlit two-bedroom off Kira Road"
            required
          />
        </Field>
        <Field label="Description (min 20 chars)" required>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="input min-h-[120px] py-3"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price / night (Ush)" required>
            <input
              type="number"
              min={10}
              value={form.price_per_night}
              onChange={(e) => setForm({ ...form, price_per_night: Number(e.target.value) })}
              className="input"
              required
            />
          </Field>
          {/*
            What a guest gets. This is the facet discovery filters on, so it
            has to come from the same vocabulary the server validates against.
          */}
          <Field label="Type of place" required>
            <select
              value={form.room_type}
              onChange={(e) =>
                setForm({ ...form, room_type: e.target.value as CreateListingPayload["room_type"] })
              }
              className="input"
            >
              {roomTypes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Max guests" required>
            <input
              type="number"
              min={1}
              max={50}
              value={form.max_guests}
              onChange={(e) => setForm({ ...form, max_guests: Number(e.target.value) })}
              className="input"
              required
            />
          </Field>
          <Field label="Bedrooms" required>
            <input
              type="number"
              min={0}
              max={20}
              value={form.bedrooms ?? 0}
              onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })}
              className="input"
              required
            />
          </Field>
          {/*
            Beds is not the same number as bedrooms — a room with two singles
            sleeps differently from one with a double, and guests compare on it.
            The column and the card have always shown it; nothing could set it.
          */}
          <Field label="Beds" required>
            <input
              type="number"
              min={1}
              max={50}
              value={form.beds ?? 1}
              onChange={(e) => setForm({ ...form, beds: Number(e.target.value) })}
              className="input"
              required
            />
          </Field>
          <Field label="Bathrooms" required>
            <input
              type="number"
              min={0}
              max={20}
              step="0.5"
              value={form.bathrooms ?? 0}
              onChange={(e) => setForm({ ...form, bathrooms: Number(e.target.value) })}
              className="input"
              required
            />
          </Field>
        </div>

        <ImageUploader
          label="Listing photos"
          value={form.images ?? []}
          onChange={(images) => setForm({ ...form, images })}
          max={10}
        />

        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            Amenities
          </span>
          <PillPicker
            options={AMENITY_OPTIONS.map((a) => a.label)}
            value={form.amenities ?? []}
            onChange={(amenities) => setForm({ ...form, amenities })}
            placeholder="Add another amenity…"
          />
        </div>

        <div>
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            House rules
          </span>
          <PillPicker
            options={HOUSE_RULE_OPTIONS}
            value={form.house_rules ?? []}
            onChange={(house_rules) => setForm({ ...form, house_rules })}
            placeholder="Add another rule…"
          />
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
            Cancellation policy
          </label>
          <div className="grid gap-2">
            {CANCELLATION_POLICIES.map((policy) => {
              const selected = (form.cancellation_policy ?? "moderate") === policy.value;
              return (
                <button
                  key={policy.value}
                  type="button"
                  onClick={() => setForm({ ...form, cancellation_policy: policy.value })}
                  aria-pressed={selected}
                  className={[
                    "rounded-xl border px-3 py-2.5 text-left transition",
                    selected
                      ? "border-primary bg-primary/5"
                      : "border-border bg-background hover:border-primary/30",
                  ].join(" ")}
                >
                  <span
                    className={`block text-sm font-bold ${selected ? "text-primary" : "text-ink"}`}
                  >
                    {policy.label}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-relaxed text-ink-muted">
                    {policy.summary}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-ink-subtle">
            Guests see this on your listing before they book. Stricter terms protect your calendar;
            looser ones tend to get booked more.
          </p>
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.instant_book ?? false}
              onChange={(e) => setForm({ ...form, instant_book: e.target.checked })}
            />
            Instant book
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active (visible to guests)
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate({ to: "/host/listings" })}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-bold text-ink hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {create.isPending ? "Creating…" : "Create listing"}
          </button>
        </div>
      </form>

      <style>{`.input{display:block;width:100%;height:42px;border-radius:10px;border:1px solid var(--input);background:var(--background);padding:0 12px;font-size:14px;color:var(--ink);outline:none;} .input:focus{border-color:var(--primary);} textarea.input{height:auto;}`}</style>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-ink-muted">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      {children}
    </label>
  );
}
